import { Card, CardBody, CardFooter, Col, Row, Table } from "reactstrap";
import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import ButtonIcon from "../common/ButtonIcon";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useSelector } from "react-redux";
import SupportLink from "../root/support/components/SupportLink";
import NumberFormat from "react-number-format";
// eslint-disable-next-line import/no-named-as-default
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import {
  fetchReportsProperties,
  findOneConfig,
} from "../../helpers/api/condominium";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { CurrencyContext } from "../../context/Context";
import { isIterableArray } from "../../helpers/utils";
import FullPagination from "../common/pagination/FullPagination";
import Loader from "../common/Loader";

const Money = ({ value, prefix, emphasize = false }) => {
  const amount = Number(value || 0);
  const isZero = Math.abs(amount) < 0.005;

  return (
    <NumberFormat
      className={`text-nowrap d-block ${
        isZero ? "text-400" : emphasize ? "font-weight-semi-bold text-danger" : ""
      }`}
      displayType="text"
      prefix={prefix}
      allowedDecimalSeparators={true}
      decimalScale={2}
      fixedDecimalScale
      thousandSeparator={true}
      value={amount}
    />
  );
};

Money.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefix: PropTypes.string,
  emphasize: PropTypes.bool,
};

const sheetStyles = `
  .debt-sheet-scroll {
    max-height: 68vh;
    overflow: auto;
    border: 1px solid #edf2f9;
    border-radius: 6px;
  }
  .debt-sheet {
    border-collapse: separate;
    border-spacing: 0;
    margin-bottom: 0;
  }
  .debt-sheet th,
  .debt-sheet td {
    background: #fff;
    border-bottom: 1px solid #edf2f9;
    vertical-align: middle;
  }
  .debt-sheet thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f9fafd;
    color: #5e6e82;
    font-size: 11px;
    letter-spacing: 0.01em;
    box-shadow: inset 0 -1px 0 #d8e2ef;
  }
  .debt-sheet .sticky-property,
  .debt-sheet .sticky-total {
    position: sticky;
    z-index: 2;
  }
  .debt-sheet .sticky-property {
    left: 0;
    min-width: 148px;
    max-width: 180px;
  }
  .debt-sheet .sticky-total {
    left: 148px;
    min-width: 148px;
    box-shadow: 8px 0 8px -8px rgba(44, 62, 80, 0.28);
  }
  .debt-sheet thead .sticky-property,
  .debt-sheet thead .sticky-total {
    z-index: 4;
    background: #f9fafd;
  }
  .debt-sheet tbody tr:hover td {
    background: #f4f7fb;
  }
  .debt-sheet .month-col {
    min-width: 108px;
  }
  @media print {
    .debt-print {
      font-size: 11pt;
    }
    .debt-sheet-scroll {
      max-height: none;
      overflow: visible;
      border: 0;
    }
    .debt-sheet {
      width: 100% !important;
      table-layout: fixed;
      font-size: 11pt !important;
    }
    .debt-sheet th,
    .debt-sheet td,
    .debt-sheet .sticky-property,
    .debt-sheet .sticky-total,
    .debt-sheet .month-col {
      position: static !important;
      left: auto !important;
      min-width: 0 !important;
      max-width: none !important;
      width: auto;
      box-shadow: none;
      font-size: 11pt !important;
      line-height: 1.25;
      padding: 5px 4px !important;
      white-space: normal;
    }
    .debt-sheet thead th {
      font-size: 10pt !important;
      color: #344050;
    }
    .debt-sheet .text-nowrap {
      white-space: nowrap !important;
      font-size: 10pt !important;
    }
    .debt-sheet .sticky-property {
      width: 14%;
    }
    .debt-sheet .sticky-total {
      width: 12%;
    }
  }
`;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatPrintMoney = (value, symbol) => {
  const amount = Number(value || 0);
  const formatted = amount.toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${symbol || ""} ${formatted}`.trim();
};

const ReportsProperties = () => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { data: dataConfig, isLoading: loadingConfig } = useSWRFetch(
    selectedCondominium,
    findOneConfig
  );
  const { condominium, address, phone } = useSelector(
    (state) => state.condominium
  );
  const { baseCurrency } = useContext(CurrencyContext);
  const [paramsP, setParamsP] = useState({
    condominiumId: selectedCondominium,
    search: null,
  });
  const {
    data: dataProperty,
    error,
    isLoading,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchReportsProperties, paramsP, null, 1, 50);

  const sheetRows = dataProperty;
  const sortedPeriods = sheetRows[0]?.latest_periods || [];

  const handlePrint = () => {
    const symbol = baseCurrency?.symbol || "";
    const monthHeaders = sortedPeriods
      .map((period) => {
        const [month, ...rest] = String(period.name || "").split(" ");
        const year = rest.join(" ");

        return `<th class="month">${escapeHtml(month)}${
          year ? `<br>${escapeHtml(year)}` : ""
        }</th>`;
      })
      .join("");
    const bodyRows = sheetRows
      .map((cuenta) => {
        const cells = sortedPeriods
          .map((period) => {
            const debt = (cuenta.debt_by_period || []).find(
              (item) => String(item.id) === String(period.id)
            );
            const amount = Number(debt?.total_debt || 0);
            const tone = Math.abs(amount) < 0.005 ? "zero" : "due";

            return `<td class="month"><span class="${tone}">${formatPrintMoney(amount, symbol)}</span></td>`;
          })
          .join("");

        const totalTone = Math.abs(Number(cuenta.total_debts) || 0) < 0.005 ? "zero" : "due";

        return `<tr>
          <td class="property">${escapeHtml(
            String(cuenta?.property ?? "").trim() || "Sin inmueble"
          )}</td>
          <td class="total">
            <span class="${totalTone}">${formatPrintMoney(cuenta.total_debts, symbol)}</span>
          </td>
          ${cells}
        </tr>`;
      })
      .join("");

    const landscape = sortedPeriods.length > 5;
    const pageWidth = landscape ? "281mm" : "194mm";
    const pageSize = landscape ? "297mm 210mm" : "210mm 297mm";
    const frame = document.createElement("iframe");
    frame.setAttribute("title", "Imprimir deuda acumulada");
    frame.style.position = "fixed";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);

    const printWindow = frame.contentWindow;
    const printDocument = printWindow.document;
    printDocument.open();
    printDocument.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Deuda acumulada</title>
  <style>
    @page { size: ${pageSize}; margin: 8mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body {
      margin: 0;
      width: ${pageWidth};
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11pt;
      color: #1e293b;
    }
    h1 { font-size: 16pt; margin: 0 0 4px; }
    p { margin: 0 0 2px; }
    table {
      width: 100%;
      margin-top: 12px;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 10pt;
    }
    th, td {
      border-bottom: 1px solid #d8e2ef;
      padding: 2px 2px;
      text-align: right;
      vertical-align: middle;
      line-height: 1.1;
    }
    th { font-size: 8pt; background: #f9fafd; color: #5e6e82; }
    .property { width: ${landscape ? "8%" : "16%"}; text-align: left; font-weight: 600; color: #0b1727; padding-right: 2px; }
    th.property { text-align: left; }
    th.total, td.total { width: ${landscape ? "11%" : "18%"}; text-align: right; padding-left: 2px; }
    th.month { white-space: normal; line-height: 1.05; font-size: 7.5pt; }
    td.month { white-space: nowrap; font-size: 8pt; }
    .due { color: #e63757; font-weight: 600; }
    .zero { color: #9da9bb; font-weight: 400; }
    .total .due, .total .zero { display: block; line-height: 1.15; }
  </style>
</head>
<body>
  <h1>Deuda acumulada</h1>
  <p><strong>${escapeHtml(condominium)}</strong></p>
  <p>${escapeHtml(address || "")}</p>
  <table>
    <thead>
      <tr>
        <th class="property">Inmueble</th>
        <th class="total">Acumulado</th>
        ${monthHeaders}
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`);
    printDocument.close();

    const printAndRemove = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => frame.remove(), 1000);
    };

    if (printDocument.readyState === "complete") {
      setTimeout(printAndRemove, 200);
    } else {
      frame.onload = () => setTimeout(printAndRemove, 200);
    }
  };

  return (
    <>
      <PageHeader
        title="Deuda acumulada"
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>

      {!isLoading && dataProperty.length > 0 && (
        <Card className="mb-3 d-print-none">
          <CardBody>
            <Row className="justify-content-between align-items-center d-flex">
              <Col md>
                <h5 className="mb-2 mb-md-0">Imprimir planilla</h5>
              </Col>
              <Col
                xs="auto"
                lg={"auto"}
                md={12}
                className="justify-self-end d-flex justify-content-end align-items-center"
              >
                <ButtonIcon
                  onClick={handlePrint}
                  color="falcon-default"
                  size="sm"
                  icon="print"
                  className="mr-2 mb-2 mb-sm-0"
                >
                  Imprimir
                </ButtonIcon>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      <Card className="mb-3 debt-print">
        <style>{sheetStyles}</style>
        <CardBody className="mb-0">
          <ItemImageTitleSubProperty
            image={dataConfig?.data?.logo}
            title1={"Deuda acumulada"}
            titleCondominium={`Condominio: ${condominium}`}
            condominium={[
              {
                label: "Documento de identificación",
                value: phone || "No disponible",
              },

              { label: "Dirección", value: address },
            ]}
            loading={loadingConfig}
          />

          {!isLoading ? (
            <>
              {sheetRows.length > 0 ? (
                <>
                  <p className="fs--1 text-600 mb-2 d-print-none">
                    {sortedPeriods.length > 0
                      ? `${sortedPeriods.length} ${
                          sortedPeriods.length === 1 ? "mes" : "meses"
                        } con deuda pendiente. El inmueble y el acumulado se quedan fijos al desplazar.`
                      : "Ningún mes tiene deuda pendiente."}
                  </p>
                  <div className="debt-sheet-scroll fs--1 mb-0">
                    <Table size="sm" className="debt-sheet">
                      <thead>
                        <tr>
                          <th className="border-0 sticky-property">Inmueble</th>
                          <th className="border-0 text-right sticky-total">
                            Acumulado
                          </th>
                          {sortedPeriods.map((period) => (
                            <th
                              key={period.id}
                              className="border-0 text-nowrap text-right month-col"
                            >
                              {period.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sheetRows.map((cuenta) => (
                          <tr key={cuenta.id ?? cuenta.property}>
                            <td className="sticky-property">
                              <span className="font-weight-semi-bold text-dark">
                                {String(cuenta?.property ?? "").trim() ||
                                  "Sin inmueble"}
                              </span>
                            </td>
                            <td className="text-right sticky-total">
                              <Money
                                value={cuenta.total_debts}
                                prefix={`${baseCurrency.symbol} `}
                                emphasize
                              />
                            </td>
                            {sortedPeriods.map((period) => {
                              const debt = (cuenta.debt_by_period || []).find(
                                (item) => String(item.id) === String(period.id)
                              );

                              return (
                                <td key={period.id} className="text-right month-col">
                                  <Money
                                    value={debt?.total_debt ?? 0}
                                    prefix={`${baseCurrency.symbol} `}
                                    emphasize
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <p className="text-center text-600 mb-0 py-4">
                  No hay inmuebles para mostrar.
                </p>
              )}
              {isIterableArray(dataProperty) && (
                <CardFooter className="d-print-none">
                  <FullPagination
                    nextPage={nextPage}
                    prevPage={previousPage}
                    goToPage={goToPage}
                    meta={meta}
                    className={"shadow-none"}
                    posText={"inmuebles"}
                    perPage={perPage}
                  />
                </CardFooter>
              )}
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <Loader padding="py-5" />
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

ReportsProperties.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(ReportsProperties);
